import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';
import { GraduationCap, TrendingUp, PieChart, Lightbulb, BookOpen, IndianRupee, Globe, Award } from 'lucide-react';

const InteractiveEducationPlanner = () => {
  const [inputs, setInputs] = useState({
    childAge: 5,
    educationAge: 18,
    educationType: 'india_engineering',
    currentCost: 2000000,
    expectedReturn: 12,
    currentSavings: 0
  });
  const [result, setResult] = useState(null);

  const educationCosts = {
    india_engineering: { name: 'Engineering (India)', cost: 2000000, duration: '4 Years' },
    india_medical: { name: 'Medical (India)', cost: 5000000, duration: '5.5 Years' },
    india_mba: { name: 'MBA (India - Top B-School)', cost: 3000000, duration: '2 Years' },
    abroad_undergrad: { name: 'Undergraduate (Abroad)', cost: 8000000, duration: '4 Years' },
    abroad_masters: { name: 'Masters (Abroad)', cost: 5000000, duration: '2 Years' },
    abroad_mba: { name: 'MBA (Abroad - Top B-School)', cost: 15000000, duration: '2 Years' }
  };

  const calculateEducationFund = useCallback(() => {
    const yearsToEducation = parseInt(inputs.educationAge) - parseInt(inputs.childAge);
    if (yearsToEducation <= 0) {
      setResult(null);
      return;
    }

    const currentCost = parseFloat(inputs.currentCost);
    const currentSavings = parseFloat(inputs.currentSavings || 0);
    const educationInflation = 0.10;
    const r = parseFloat(inputs.expectedReturn) / 12 / 100;
    const n = yearsToEducation * 12;

    const futureCost = currentCost * Math.pow(1 + educationInflation, yearsToEducation);
    const target = futureCost - currentSavings;

    if (r === 0 || n === 0) {
      setResult({
        monthlySIP: Math.round(target / Math.max(n, 1)),
        totalInvested: Math.round(target),
        wealthGained: 0,
        futureCost: Math.round(futureCost),
        currentCost: currentCost,
        yearsToEducation: yearsToEducation,
        inflationImpact: Math.round(futureCost - currentCost)
      });
      return;
    }

    const monthlySIP = target * r / (((Math.pow(1 + r, n) - 1)) * (1 + r));
    const totalInvested = monthlySIP * n;
    const wealthGained = target - totalInvested;

    setResult({
      monthlySIP: Math.round(monthlySIP),
      totalInvested: Math.round(totalInvested),
      wealthGained: Math.round(wealthGained),
      futureCost: Math.round(futureCost),
      currentCost: currentCost,
      yearsToEducation: yearsToEducation,
      inflationImpact: Math.round(futureCost - currentCost)
    });
  }, [inputs]);

  useEffect(() => {
    calculateEducationFund();
  }, [calculateEducationFund]);

  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `\u20b9${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      return `\u20b9${(value / 100000).toFixed(2)} L`;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleEducationTypeChange = (type) => {
    setInputs({
      ...inputs,
      educationType: type,
      currentCost: educationCosts[type].cost
    });
  };

  const sliderStyles = `
    .slider-blue::-webkit-slider-thumb {
      appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #3b82f6, #06b6d4);
      cursor: pointer;
      box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
    }
    .slider-blue::-moz-range-thumb {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #3b82f6, #06b6d4);
      cursor: pointer;
      box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
      border: none;
    }
  `;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      <style dangerouslySetInnerHTML={{ __html: sliderStyles }} />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl mb-6">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Education Planning Calculator
            </h1>
            <p className="text-lg text-white/70 max-w-3xl mx-auto">
              Secure your child&apos;s future with smart education planning. Start early, invest wisely!
            </p>
          </div>

          <div className="mb-8">
            <p className="text-white/60 text-sm mb-3 text-center">Select Education Goal:</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-4xl mx-auto">
              {Object.entries(educationCosts).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() => handleEducationTypeChange(key)}
                  className={`px-4 py-3 border rounded-xl text-sm transition-all text-left ${
                    inputs.educationType === key
                      ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                      : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {key.includes('abroad') ? <Globe className="w-4 h-4" /> : <BookOpen className="w-4 h-4" />}
                    <span className="font-medium">{value.name}</span>
                  </div>
                  <p className="text-xs opacity-60">{formatCurrency(value.cost)} \u2022 {value.duration}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                <BookOpen className="w-6 h-6 text-blue-400" />
                Child &amp; Education Details
              </h2>
              
              <div className="space-y-8">
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Child&apos;s Current Age</label>
                    <span className="text-blue-400 font-bold text-xl">{inputs.childAge} Years</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="15"
                    step="1"
                    value={inputs.childAge}
                    onChange={(e) => setInputs({...inputs, childAge: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-blue"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>0 (Newborn)</span>
                    <span>15 Years</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Education Starts At Age</label>
                    <span className="text-cyan-400 font-bold text-xl">{inputs.educationAge} Years</span>
                  </div>
                  <input
                    type="range"
                    min="16"
                    max="25"
                    step="1"
                    value={inputs.educationAge}
                    onChange={(e) => setInputs({...inputs, educationAge: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-blue"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>16 Years</span>
                    <span>25 Years</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Current Education Cost</label>
                    <span className="text-purple-400 font-bold text-xl">{formatCurrency(inputs.currentCost)}</span>
                  </div>
                  <input
                    type="range"
                    min="500000"
                    max="30000000"
                    step="100000"
                    value={inputs.currentCost}
                    onChange={(e) => setInputs({...inputs, currentCost: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-blue"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>\u20b95 L</span>
                    <span>\u20b93 Cr</span>
                  </div>
                </div>

                <div>
                  <label className="text-white font-medium mb-2 block">Current Savings (if any)</label>
                  <input
                    type="number"
                    value={inputs.currentSavings}
                    onChange={(e) => setInputs({...inputs, currentSavings: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    placeholder="0"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Expected Return (p.a.)</label>
                    <span className="text-green-400 font-bold text-xl">{inputs.expectedReturn}%</span>
                  </div>
                  <input
                    type="range"
                    min="8"
                    max="15"
                    step="0.5"
                    value={inputs.expectedReturn}
                    onChange={(e) => setInputs({...inputs, expectedReturn: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-blue"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>8%</span>
                    <span>15%</span>
                  </div>
                </div>
              </div>
            </div>

            {result ? (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 backdrop-blur-xl rounded-3xl p-8 border border-blue-500/30">
                  <p className="text-white/60 text-sm mb-2">\ud83c\udf93 Education Fund for {educationCosts[inputs.educationType].name}</p>
                  <div className="flex items-center gap-3 mb-4">
                    <IndianRupee className="w-6 h-6 text-blue-400" />
                    <h3 className="text-white/70 text-sm">Monthly SIP Required</h3>
                  </div>
                  <p className="text-5xl font-bold text-white mb-2">{formatCurrency(result.monthlySIP)}</p>
                  <p className="text-white/50 text-sm">for {result.yearsToEducation} years</p>
                </div>

                <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 backdrop-blur-xl rounded-2xl p-6 border border-orange-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/60 text-sm">\ud83d\udcc8 Cost After {result.yearsToEducation} Years</p>
                      <p className="text-3xl font-bold text-white mt-1">{formatCurrency(result.futureCost)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white/60 text-sm">Inflation Impact</p>
                      <p className="text-xl font-bold text-orange-400">+{formatCurrency(result.inflationImpact)}</p>
                    </div>
                  </div>
                  <p className="text-white/50 text-xs mt-3">* Assuming 10% annual education inflation</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <PieChart className="w-4 h-4 text-white/50" />
                      <p className="text-white/50 text-xs">Your Investment</p>
                    </div>
                    <p className="text-2xl font-bold text-white">{formatCurrency(result.totalInvested)}</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      <p className="text-white/50 text-xs">Returns Earned</p>
                    </div>
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(result.wealthGained)}</p>
                  </div>
                </div>

                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                  <p className="text-white/70 text-sm mb-3">Investment vs Returns</p>
                  <div className="h-4 bg-white/10 rounded-full overflow-hidden flex">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                      style={{width: `${(result.totalInvested / result.futureCost) * 100}%`}}
                    ></div>
                    <div 
                      className="bg-gradient-to-r from-cyan-500 to-cyan-600 transition-all duration-500"
                      style={{width: `${(result.wealthGained / result.futureCost) * 100}%`}}
                    ></div>
                  </div>
                  <div className="flex justify-between mt-2 text-xs">
                    <span className="text-blue-400">Investment: {((result.totalInvested / result.futureCost) * 100).toFixed(1)}%</span>
                    <span className="text-cyan-400">Returns: {((result.wealthGained / result.futureCost) * 100).toFixed(1)}%</span>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 backdrop-blur-xl rounded-2xl p-6 border border-amber-500/30">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-400 font-semibold mb-2">Education Fund Strategy</p>
                      <p className="text-white/80 text-sm">
                        {result.yearsToEducation <= 5 
                          ? "With education approaching soon, focus on capital preservation. Consider hybrid or debt funds with systematic transfer to equity."
                          : result.yearsToEducation <= 10
                          ? "You have a good runway! Start with 70% equity allocation and gradually shift to debt funds as the goal approaches."
                          : "With 10+ years, maximize growth with aggressive equity funds. The power of compounding will work wonders for your child's future!"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 flex items-center justify-center">
                <div className="text-center">
                  <Award className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">Please ensure education age is greater than current age</p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-full hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] transition-all duration-300"
            >
              Get Expert Education Planning Advice
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InteractiveEducationPlanner;
